import { ReactNode } from 'react'
import { useRouter } from 'next/router'
import { motion } from 'framer-motion'
import JsonLd from '@comp/structured-data'
import Container from '@comp/ui/container'
import Section from '@comp/ui/section'
import Eyebrow from '@comp/ui/eyebrow'
import Heading from '@comp/ui/heading'
import Text from '@comp/ui/text'

const EASE = [0.22, 1, 0.36, 1] as const

const internal = {
  tip: { en: 'Tip \u2014 ', tr: '\u0130pucu \u2014 ' },
  fullPicture: {
    en: 'Ready for the full picture?',
    tr: 'Tam resme haz\u0131r m\u0131s\u0131n\u0131z?',
  },
  fullPictureBody: {
    en: 'The complete walkthrough \u2014 with every screenshot, every flag, and version-specific notes \u2014 lives in our help center.',
    tr: 'Eksiksiz rehber \u2014 t\u00fcm ekran g\u00f6r\u00fcnt\u00fcleri, t\u00fcm parametreler ve s\u00fcr\u00fcme \u00f6zel notlarla \u2014 yard\u0131m merkezimizde.',
  },
}

export interface HowToStep {
  title: string
  body: string
  hint?: string
}

interface HowToStepsProps {
  eyebrow?: string
  title: ReactNode
  body?: string
  steps: HowToStep[]
  docHref: string
  docLabel?: string
  /** Plain-text guide name; when set, a schema.org HowTo block is emitted. */
  schemaName?: string
}

export default function HowToSteps({
  eyebrow = 'Walkthrough',
  title,
  body,
  steps,
  docHref,
  docLabel = 'Continue in the full docs',
  schemaName,
}: HowToStepsProps) {
  const { locale } = useRouter()
  const lang = locale === 'tr' ? 'tr' : 'en'
  return (
    <Section tone="default" density="default">
      {schemaName && (
        <JsonLd
          id="howto"
          data={{
            '@type': 'HowTo',
            name: schemaName,
            ...(body ? { description: body } : {}),
            step: steps.map((s, i) => ({
              '@type': 'HowToStep',
              position: i + 1,
              name: s.title,
              text: s.body,
            })),
          }}
        />
      )}
      <Container size="xl">
        <div className="max-w-3xl">
          <Eyebrow tone="accent">{eyebrow}</Eyebrow>
          <Heading as="h2" size="display-lg" className="mt-5">
            {title}
          </Heading>
          {body && (
            <Text size="xl" tone="secondary" className="mt-6">
              {body}
            </Text>
          )}
        </div>

        <ol className="mt-14 space-y-5">
          {steps.map((step, i) => (
            <motion.li
              key={step.title}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, ease: EASE, delay: i * 0.06 }}
              className="grid gap-4 rounded-3xl border border-line-subtle bg-surface-subtle p-6 lg:grid-cols-[80px_1fr] lg:gap-8 lg:p-10"
            >
              <div className="text-display-md font-medium text-brand-900/40 tabular-nums">
                {String(i + 1).padStart(2, '0')}
              </div>
              <div>
                <h3 className="text-display-sm font-medium text-ink-primary">
                  {step.title}
                </h3>
                <Text size="md" tone="secondary" className="mt-3">
                  {step.body}
                </Text>
                {step.hint && (
                  <div className="mt-4 rounded-2xl border border-accent-200 bg-accent-50 px-4 py-3 text-body-sm text-accent-800">
                    <strong className="font-medium">{internal.tip[lang]}</strong>
                    {step.hint}
                  </div>
                )}
              </div>
            </motion.li>
          ))}
        </ol>

        <div className="mt-10 rounded-3xl border border-line-subtle bg-surface p-6 lg:p-8 shadow-card">
          <div className="flex flex-col items-start justify-between gap-3 lg:flex-row lg:items-center lg:gap-8">
            <div className="max-w-xl">
              <div className="text-body-md font-medium text-ink-primary">
                {internal.fullPicture[lang]}
              </div>
              <Text size="sm" tone="tertiary" className="mt-1">
                {internal.fullPictureBody[lang]}
              </Text>
            </div>
            <a
              href={docHref}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex flex-shrink-0 items-center gap-2 rounded-pill bg-brand-900 px-5 py-2.5 text-body-sm font-medium text-white transition hover:bg-brand-800"
            >
              {docLabel}
              <ExternalGlyph className="h-3.5 w-3.5" />
            </a>
          </div>
        </div>
      </Container>
    </Section>
  )
}

function ExternalGlyph({ className = '' }) {
  return (
    <svg className={className} viewBox="0 0 16 16" fill="none">
      <path d="M6 3h7v7M13 3 5 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}
